import React from 'react';
import { Label } from '../../components/Label';
import { FileInput } from '../../components/File';
import { CustomField, WithCustomFields } from '../../../types/custom_field/customField';
import { FileEntityType } from '../../../types/file';
import { t } from '../../../types/translation/Translator';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import ImagesPreviewsGrid from '../ImagePreviewGrid/ImagePreviewGrid';
import { useFileUpload } from '../../../hooks/useFileUpload';

const CUSTOM_FIELD_FILE_SEPARATOR = '::';

interface CustomFieldInputImageProps {
  entity: WithCustomFields;
  entityType: FileEntityType;
  disabled?: boolean;
  item: CustomField;
  handleChange: (item: CustomField, value: any) => void;
}

export default function CustomFieldInputImage({
  entity,
  entityType,
  disabled = false,
  item,
  handleChange,
}: CustomFieldInputImageProps) {
  const { uploadFiles } = useFileUpload({
    entityId: item.id,
    entityType,
    setError: (msg: string) => {
      console.error('Error uploading files:', msg);
    },
  });

  const extractCustomFieldAndUploadImages = async (filesToUpload: File[]) => {
    const filesArray = Array.isArray(filesToUpload) ? filesToUpload : [];
    if (!filesArray || !Array.isArray(filesArray) || filesArray.length === 0) return;

    const uploadedFileIds = await uploadFiles(filesArray);

    if (!uploadedFileIds.length) return;
    return uploadedFileIds;
  };

  const handleFiles = async (filesToUpload: FileList | null) => {
    if (!filesToUpload) return;

    const uploadedFileIds = await extractCustomFieldAndUploadImages(Array.from(filesToUpload));
    if (!uploadedFileIds || !uploadedFileIds.length) return;
    const allIds = Array.from([...attachedImages, ...uploadedFileIds]);
    handleChange(item, allIds.join(CUSTOM_FIELD_FILE_SEPARATOR));
  };

  const handleRemove = (removeId: string) => {
    const newIds = [...attachedImages].filter(id => id !== removeId);
    handleChange(item, newIds.join(CUSTOM_FIELD_FILE_SEPARATOR));
  };

  const cfImageValue = entity.customFields.get(item.id)?.value;
  const attachedImages = cfImageValue?.split(CUSTOM_FIELD_FILE_SEPARATOR) || [];

  if (disabled) {
    if (!attachedImages?.length) return null;

    return (
      <>
        <p className='mt-1 text-sm font-medium text-slate-800'>{item.name}</p>
        <ImagesPreviewsGrid
          fileEntityType={entityType}
          cfImageValue={cfImageValue}
          attachedImages={attachedImages}
          handleRemove={handleRemove}
          disabled={disabled}
        />
      </>
    );
  }

  return (
    <div className='flex flex-col'>
      <Label label={t(TranslationKey.image).plural.label} mandatory={item.mandatory}>
        <FileInput multiple onFiles={handleFiles} acceptedTypes={'.jpg,.jpeg,.png'} />
      </Label>
      <div className='py-1'></div>
      <ImagesPreviewsGrid
        disabled={disabled}
        cfImageValue={cfImageValue}
        attachedImages={attachedImages}
        handleRemove={handleRemove}
        fileEntityType={entityType}
      />
    </div>
  );
}
